/* eslint-disable max-len */
import React, { useLayoutEffect, useMemo, useRef } from "react";
import { Box, Typography, createSvgIcon, useTheme, useMediaQuery } from "@mui/material";
import { styled } from "@mui/material/styles";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import { PulseCircle } from "../FlexComponents";
import { LandToken } from "../../types/LandToken";
import { formatValue } from "../../utils/formatValue";
import { landImageSrc } from "../../utils/landImage";
import { numberWithCommas, sixDigits } from "../../utils/methods";
import { landDetailLink } from "../../config/app.routes";
import { useNavigate } from "react-router-dom";
import EditionBadgeCanvas from "../EditionBadge";
import { AnimatedLandLayer } from "../AnimatedLand";
import { resolveStandardStatus } from "../../utils/standard-status";
import { landStatusStates } from "../../utils/landMeta";
import StatusCycle from "../StatusCycle";
import {
  MORPH_TO_DETAIL_KEY,
  MORPH_TO_POPUP_KEY,
  playMorphFromRect,
  stashMorphRect,
  takeMorphRect,
} from "../../utils/flip-morph";

export interface LandCardPopupProps {
  open: boolean;
  onClose: () => void;
  token?: LandToken;
  // When provided (Explore inline mode), clicking the card reveals the detail
  // inline at the given origin instead of navigating to the standalone route.
  onOpenDetail?: (origin: { x: number; y: number }) => void;
  // Render the card in normal document flow (no fixed full-screen overlay) so it
  // can be embedded as a hero inside another scroll container — used as the mobile
  // preview at the top of the inline land detail.
  inline?: boolean;
}

const BUTTON_HEIGHT = 51;

const Overlay = styled(Box)({
  position: "fixed",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1300,
  overflowY: "hidden",
  "@media (max-height: 620px)": {
    alignItems: "flex-start",
    padding: "16px 0",
  },
});

const CardBox = styled(Box)({
  position: "relative",
  width: 300,
  height: 321,
  borderRadius: 20,
  backgroundColor: "transparent",
  overflow: "hidden",
  boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
  cursor: "default",
});

const HeroImage = styled(Box, { shouldForwardProp: (prop) => prop !== "imageUrl" })<{
  imageUrl: string;
  left?: number;
  top?: number;
  width?: number | string;
  height?: number | string;
}>(({ imageUrl, left, top, width, height }) => ({
  position: "absolute",
  ...(left !== undefined ? { left } : {}),
  ...(top !== undefined ? { top } : {}),
  ...(width !== undefined ? { width } : {}),
  ...(height !== undefined ? { height } : {}),
  backgroundImage: `url('${imageUrl}')`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  borderRadius: 20,
  cursor: "pointer",
}));

export const BulletList = styled("ul")({
  margin: 0,
  paddingLeft: 18,
  fontFamily: "Inter, sans-serif",
  fontSize: 10,
  lineHeight: "1.4em",
  color: "#000",
});

export const BulletItem = styled("li")({ marginBottom: 4 });

// Sale-bar summary (Current Bid / Highest Offer / Last Sale) for a token, or
// null when the bar shouldn't render. Exported so Explore's marker→card bloom
// can size its open-card geometry to whether the bar will be there.
export const getSaleDescription = (token?: LandToken): { label: string; amount: string } | null => {
  const isLiveAuction = token?.etNft?.etAuction?.status === "started" || token?.etNft?.status === "auction";
  const isLiveOffer = token?.offerStatus === "opened";
  const isSold =
    !!token?.etNft?.ownerAddress &&
    !!token?.etNft?.minterAddress &&
    token.etNft.ownerAddress !== token.etNft.minterAddress;

  const formatWei = (value: unknown): string | null =>
    value === undefined || value === null || value === ""
      ? null
      : String(sixDigits(formatValue(value as any, true, 2)));

  const formatPlain = (value: unknown): string | null =>
    value === undefined || value === null || value === ""
      ? null
      : String(sixDigits(formatValue(Number(value), true, 2)));

  if (isLiveAuction) {
    const bids = token?.etNft?.etAuction?.bids ?? [];
    if (bids.length > 0) {
      const formatted = formatWei(bids[bids.length - 1].amount);
      if (formatted) {
        return { label: "Current Bid", amount: "$" + formatted };
      }
    } else {
      const formatted = formatWei(token?.etNft?.etAuction?.startingPrice);
      if (formatted) {
        return { label: "Starting Bid", amount: "$" + formatted };
      }
    }
  } else if (isLiveOffer) {
    const offers: Array<{ amount: string; txValue?: number }> = (token as any)?.offers ?? [];
    const highest = offers.reduce<{ amount: string; txValue?: number } | undefined>(
      (max, o) => (Number(o?.amount ?? 0) > Number(max?.amount ?? 0) ? o : max),
      undefined
    );
    const offerAmount = highest ? Number(highest.txValue ?? highest.amount) : 0;
    if (offerAmount > 0) {
      const formatted = formatPlain(offerAmount);
      if (formatted) {
        return { label: "Highest Offer", amount: "$" + formatted };
      }
    }
  }

  if (isSold) {
    const formatted = formatWei(token?.etNft?.etAuction?.endingPrice);
    if (formatted) {
      return { label: "Last Sale", amount: "$" + formatted };
    }
  }

  return null;
};

const LandCardPopup: React.FC<LandCardPopupProps> = ({ open, onClose, token, onOpenDetail, inline }) => {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Reverse shared-element morph: when the inline detail collapses back to this
  // popup, Explore stashes the detail hero's last rect — FLIP the card from it
  // down to its natural position. No stash (plain popup open) → no animation.
  useLayoutEffect(() => {
    if (!open || inline || !cardRef.current) {
      return;
    }
    const from = takeMorphRect(MORPH_TO_POPUP_KEY, token?.id);
    if (from) {
      playMorphFromRect(cardRef.current, from);
    }
  }, [open, inline, token?.id]);

  const onOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) onClose();
  };

  const onOverlayKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape" || e.key === "Enter" || e.key === " ") onClose();
  };

  if (!open) return null;

  const sale = getSaleDescription(token);

  const cardContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }} onClick={(e) => e.stopPropagation()}>
      <CardBox ref={cardRef} className="ef-cc-card" role="dialog" aria-modal="true">
        <StatusViewData token={token} isMobile={isMobile} onOpenDetail={onOpenDetail} />
      </CardBox>
      {sale && (
        <Box
          className="ef-cc-sale"
          sx={{
            backgroundColor: "#BC92F8",
            borderRadius: "20px",
            p: { xs: "18px", md: "24px" },
            height: BUTTON_HEIGHT,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            textTransform: "uppercase",
          }}
        >
          <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: 14 }}>{sale.label}</Typography>
          <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: 14 }}>{sale.amount}</Typography>
        </Box>
      )}
    </Box>
  );

  // Embedded hero: render the card in normal flow (no fixed overlay) so it can sit
  // at the top of the inline land detail's scroll container.
  if (inline) {
    return <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>{cardContent}</Box>;
  }

  return (
    <Overlay
      ref={overlayRef}
      onClick={onOverlayClick}
      onKeyDown={onOverlayKeyDown}
      role="button"
      aria-label="Close popup"
      tabIndex={0}
      sx={{ top: { xs: "135px" } }}
    >
      {cardContent}
    </Overlay>
  );
};

const StatusViewData: React.FC<{
  token?: LandToken;
  isMobile: boolean;
  onOpenDetail?: (origin: { x: number; y: number }) => void;
}> = ({ token, isMobile, onOpenDetail }) => {
  const navigate = useNavigate();

  const handle = (e?: React.MouseEvent | React.KeyboardEvent) => {
    // Explore inline mode: reveal the detail in place (origin = click point for
    // the desktop slide/stagger animation) instead of leaving the page.
    if (onOpenDetail) {
      // Shared-element morph: remember where this card sits so the detail hero
      // can FLIP-animate from this exact rect (consumed by the embedded
      // LandPage's preview hero on mount).
      if (e?.currentTarget instanceof HTMLElement) {
        stashMorphRect(MORPH_TO_DETAIL_KEY, token?.id, e.currentTarget.getBoundingClientRect());
      }
      const origin =
        e && "clientX" in e
          ? { x: (e as React.MouseEvent).clientX, y: (e as React.MouseEvent).clientY }
          : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
      onOpenDetail(origin);
      return;
    }
    // Reroute to the inline detail on Explore (the standalone LandPage route is
    // no longer the in-app destination).
    if (token?.id) navigate(landDetailLink(token.id));
  };

  // Same rotating location line as the LandPage hero: place, lon/lat, size, elevation.
  const locationStates = useMemo(() => landStatusStates(token?.meta), [token?.meta]);

  const ecoScore = token?.ecoScore != null ? (token.ecoScore * 100).toFixed(0) : "N/A";
  const totalTrees = token?.etNft?.totalTreeForEasement != null
    ? formatValue(token.etNft.totalTreeForEasement as any, false)
    : null;
  const formattedTrees = totalTrees != null ? sixDigits(formatValue(totalTrees as any, true, 0)) : null;
  const tokenNumber = token?.etNft?.id != null ? String(token.etNft.id) : null;

  const remainingTrees = token?.etNft?.easementActivated
    ? Number(formatValue(token.etNft.totalTreeForEasement as any, false)) -
      Number(formatValue(token.etNft.easementActivated.rewardClaimed as any, false) || 0)
    : null;
  const formattedRemainingTrees = remainingTrees !== null ? String(numberWithCommas(sixDigits(remainingTrees))) : null;

  const publicStatus = token
    ? resolveStandardStatus(
        { status: token.status, etNft: token.etNft, offerStatus: token.offerStatus, meta: token.meta },
        { role: "public" }
      )
    : null;

  const isGreenBadge = publicStatus?.color === "action.pulse";
  const badgeTextColor = isGreenBadge ? "#ccff99" : "rgba(255,255,255,0.6)";
  const badgeBorderColor = isGreenBadge ? "#ccff99" : "rgba(255,255,255,0.35)";

  const stats = [
    { label: "SCORE", value: ecoScore },
    { label: "TR3", value: formattedRemainingTrees ?? formattedTrees ?? "N/A" },
    { label: "No", value: tokenNumber ?? "N/A" },
  ];
  // Animated rendition, falling back to the original upload for lands minted
  // before renditions existed.
  const animatedSrc = landImageSrc(token, "full");

  return (
    <HeroImage
      left={0}
      top={0}
      width="100%"
      height="100%"
      // Static poster as the background, never the animated rendition: "full" is
      // a 30MB+ animated WebP/GIF whose first paint stalls the main thread for up
      // to ~1.4s right as the Explore bloom starts. AnimatedLandLayer plays it
      // over the poster from a worker instead.
      imageUrl={landImageSrc(token, "poster") as string}
      onClick={handle}
      sx={{ overflow: "hidden" }}
    >
      {/* First child so the scrim, badges and text all paint above it. Starts
          after Explore's 600ms marker→card bloom so the download and decode
          never compete with it (or the map's tile loads during the fly). */}
      <AnimatedLandLayer key={animatedSrc} src={animatedSrc} startDelayMs={700} />
      {token?.etNft?.editionId && (
        <Box
          sx={{
            position: "absolute",
            top: isMobile ? "18px" : "24px",
            left: isMobile ? "18px" : "24px",
            zIndex: 1000,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <EditionBadgeCanvas
            size={isMobile ? 48 : 64}
            editionId={Number(token.etNft.editionId)}
            active={remainingTrees !== null && remainingTrees > 0}
          />
        </Box>
      )}

      {/* Public status badge — top right */}
      {publicStatus && (
        <Box
          className="ef-cc-chip"
          sx={{
            position: "absolute",
            top: isMobile ? "18px" : "24px",
            right: isMobile ? "18px" : "24px",
            zIndex: 2,
            height: 36,
            px: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
            backgroundColor: "rgba(43,53,1,0.75)",
            border: `1px solid ${badgeBorderColor}`,
            borderRadius: "20px",
            color: badgeTextColor,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {publicStatus.label === "Active" && <PulseCircle sold={true} sx={{ mr: 0 }} />}
          {publicStatus.label === "Pending" && <PulseCircle warning sx={{ mr: 0 }} />}
          {(publicStatus.label === "Offer" || publicStatus.label === "Auction") && (
            <ListAltOutlinedIcon sx={{ fontSize: 14 }} />
          )}
          {publicStatus.label === "Idle" && <IdleIcon sx={{ fontSize: 14 }} />}
          <Typography
            sx={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontWeight: 700,
              fontSize: 12,
              color: badgeTextColor,
              textTransform: "uppercase",
              lineHeight: 1,
            }}
          >
            {publicStatus.label}
          </Typography>
        </Box>
      )}

      {/* Gradient blur overlay fading in from the bottom */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: "20px",
          backgroundColor: isMobile ? "rgba(43, 53, 1, 0.25)" : "rgba(43, 53, 1, 0.15)",
          backdropFilter: "blur(30px)",
          maskImage: "linear-gradient(to bottom, transparent 0%, black 85%)",
          WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 85%)",
        }}
      />

      {/* Land name and location */}
      <Box className="ef-cc-title" sx={{ position: "absolute", bottom: 68, left: 20, right: 20 }}>
        <Typography
          sx={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 26, color: "#fff", lineHeight: 1.21 }}
        >
          {token?.meta?.name ?? ""}
        </Typography>
        {/* Status line: shimmers while a state holds, then swaps to the next (StatusCycle). */}
        <Typography
          sx={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 700,
            fontSize: 16,
            color: "#fff",
            lineHeight: 1.21,
            letterSpacing: "0.08em",
            "--think-base": "rgba(255, 255, 255, 0.7)",
            "--think-highlight": "#fff",
          }}
        >
          <StatusCycle states={locationStates} />
        </Typography>
      </Box>

      {/* Stats row: SCORE | TR3 | No */}
      <Box
        className="ef-cc-stats"
        sx={{ position: "absolute", bottom: 16, left: 20, right: 20, display: "flex", justifyContent: "space-between" }}
      >
        {stats.map((stat) => (
          <Box key={stat.label}>
            <Typography
              sx={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontWeight: 700,
                fontSize: 16,
                color: "rgba(255,255,255,0.5)",
                lineHeight: 1.3,
              }}
            >
              {stat.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontWeight: 700,
                fontSize: 16,
                color: "#fff",
                lineHeight: 1.3,
              }}
            >
              {stat.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </HeroImage>
  );
};

export const Icon1 = createSvgIcon(
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9.78584" cy="9.78584" r="9.78584" fill="#BC92F8" />
    <path
      d="M13.8252 5.25781V9.78516C13.8252 11.0408 13.3839 12.1125 12.5039 12.9922C11.6929 13.803 10.7188 14.2391 9.58789 14.3027L9.60059 14.3154H9.29883C8.73876 14.3154 8.20968 14.2194 7.71289 14.0303C7.25403 13.8556 6.83621 13.6177 6.45703 13.3154L5.60742 14.166C5.55686 14.2166 5.50413 14.2537 5.44434 14.2783C5.38479 14.3028 5.32356 14.3125 5.26172 14.3125C5.12875 14.3124 5.01217 14.2611 4.91699 14.166C4.82172 14.0708 4.77051 13.9535 4.77051 13.8203C4.77055 13.7586 4.7793 13.698 4.80371 13.6387C4.82837 13.5788 4.86631 13.5263 4.91699 13.4756L5.7666 12.625C5.46666 12.2466 5.22641 11.8287 5.05176 11.3701C4.86275 10.8736 4.76758 10.3449 4.76758 9.78516C4.76759 8.52951 5.20891 7.45784 6.08887 6.57812C6.96871 5.69867 8.04018 5.25788 9.2959 5.25781H13.8252ZM9.30078 6.24121C8.31358 6.24129 7.47968 6.58595 6.78906 7.27637C6.09837 7.96687 5.75391 8.80104 5.75391 9.78809C5.754 10.7749 6.0985 11.6084 6.78906 12.2988C7.44757 12.9571 8.23632 13.3005 9.16406 13.3311H9.29883C10.286 13.331 11.1199 12.9872 11.8105 12.2969C12.5012 11.6064 12.8457 10.7722 12.8457 9.78516V6.24121H9.30078ZM10.0859 7.2002C10.1529 7.2002 10.2137 7.21933 10.2666 7.25586L10.3164 7.29785L10.3252 7.30566L10.2305 7.38379L10.3252 7.30664C10.3708 7.36208 10.4049 7.42834 10.4092 7.50879C10.4134 7.58889 10.3861 7.65761 10.3408 7.71777L10.3418 7.71875L9.28418 9.16797L11.0479 9.34277L11.0537 9.34375C11.1279 9.3548 11.1981 9.38005 11.2559 9.42676C11.3142 9.47392 11.3528 9.53682 11.375 9.6084C11.3967 9.6785 11.4015 9.75136 11.3818 9.82227C11.3622 9.89308 11.321 9.9535 11.2666 10.0029H11.2656L8.7334 12.2656L8.69824 12.2969H8.69434C8.64223 12.3357 8.58198 12.3583 8.51367 12.3584C8.42608 12.3584 8.35086 12.3215 8.29004 12.2607C8.23758 12.2083 8.19027 12.1386 8.18555 12.0488C8.18139 11.9678 8.20848 11.8976 8.25488 11.8369L9.32129 10.3887L7.55957 10.2158L7.55273 10.2148C7.47864 10.2038 7.4093 10.1775 7.35156 10.1309C7.29313 10.0836 7.25366 10.021 7.23145 9.94922C7.20984 9.87928 7.20602 9.80706 7.22559 9.73633C7.24527 9.66552 7.28643 9.6051 7.34082 9.55566L9.86133 7.29297C9.92376 7.23272 9.99931 7.20031 10.0859 7.2002Z"
      fill="#1F1F1F"
      stroke="black"
      stroke-width="0.25"
    />
  </svg>,
  "Icon1"
);

export const Icon2 = createSvgIcon(
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9.78584" cy="9.78584" r="9.78584" fill="#BC92F8" />
    <path
      d="M10.3447 6.04004V6.95898H6.18066V13.3965H12.6826V10.1641H13.6084V13.5215C13.6084 13.7401 13.5293 13.9283 13.373 14.083C13.2168 14.2376 13.0272 14.3154 12.8076 14.3154H6.05566C5.83629 14.3153 5.64731 14.2375 5.49121 14.083C5.33495 13.9282 5.2559 13.7401 5.25586 13.5215V6.83398C5.25586 6.61534 5.33491 6.42726 5.49121 6.27246C5.64734 6.11792 5.83626 6.04013 6.05566 6.04004H10.3447ZM8.02637 9.25V12.5654H7.10156V9.25H8.02637ZM9.89453 7.79004V12.5654H8.96973V7.79004H9.89453ZM11.7627 10.666V12.5654H10.8379V10.666H11.7627ZM15.292 5.25781V7.5C15.292 8.11972 15.0688 8.65119 14.6289 9.08691C14.1901 9.52155 13.6559 9.7411 13.0332 9.74219V9.74512C12.7868 9.74512 12.5469 9.70315 12.3193 9.62012C12.1266 9.5498 11.9419 9.45886 11.7637 9.35059L11.5977 9.50879L11.5947 9.51074C11.5052 9.59056 11.3988 9.63285 11.2812 9.63867C11.1521 9.64499 11.0404 9.59424 10.9512 9.50586C10.8618 9.41728 10.8125 9.30834 10.8125 9.18359C10.8125 9.05872 10.8617 8.94899 10.9512 8.86035L11.127 8.68457C11.0277 8.52323 10.9446 8.35242 10.8809 8.17285C10.804 7.95643 10.7656 7.73019 10.7656 7.5C10.7657 6.88062 10.9882 6.34962 11.4277 5.91406C11.8676 5.47841 12.4038 5.25787 13.0283 5.25781H15.292ZM13.0332 6.17871C12.6582 6.17871 12.3455 6.30633 12.0859 6.56543V6.56641C11.826 6.82386 11.6953 7.13516 11.6953 7.50293C11.6953 7.61786 11.7113 7.7314 11.7422 7.84375C11.7589 7.90432 11.7783 7.96565 11.7998 8.02734L12.959 6.88086C13.0484 6.79245 13.1584 6.74512 13.2832 6.74512C13.408 6.7452 13.518 6.79231 13.6074 6.88086C13.6983 6.97082 13.7451 7.08341 13.7451 7.21094C13.7451 7.33922 13.6975 7.44885 13.6074 7.53809L12.4541 8.67969C12.5249 8.70971 12.5961 8.73872 12.6689 8.76562C12.7561 8.79779 12.844 8.81759 12.9355 8.82617H13.0332C13.4082 8.82615 13.7209 8.6986 13.9805 8.43945L14.0723 8.33984C14.2711 8.10289 14.371 7.82444 14.3711 7.50293V6.17871H13.0332Z"
      fill="#1F1F1F"
      stroke="black"
      stroke-width="0.25"
    />
  </svg>,
  "Icon2"
);

const IdleIcon = createSvgIcon(
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960">
    <path d="M140-440q-25 0-42.5-17.5T80-500q0-25 17.5-42.5T140-560h240q25 0 42.5 17.5T440-500q0 25-17.5 42.5T380-440H140Zm440 0q-25 0-42.5-17.5T520-500q0-25 17.5-42.5T580-560h240q25 0 42.5 17.5T880-500q0 25-17.5 42.5T820-440H580Z" />
  </svg>,
  "Idle"
);

export const TrusteeIcon = createSvgIcon(
  <svg width="15" height="19" viewBox="0 0 15 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.78979 12.1197H8.96973V9.93969H11.1498V7.75969H8.96973V5.57969H6.78979V7.75969H4.60974V9.93969H6.78979V12.1197ZM7.87976 18.1097C5.83976 17.6097 4.14981 16.4497 2.80981 14.6397C1.46981 12.8297 0.799805 10.8097 0.799805 8.59969V3.39969L7.87976 0.679688L14.9597 3.39969V8.59969C14.9597 10.8097 14.2897 12.8297 12.9497 14.6397C11.6097 16.4497 9.91976 17.6097 7.87976 18.1097Z"
      fill="black"
    />
  </svg>,
  "Trustee"
);

export default LandCardPopup;
